import React, { useEffect, useRef } from 'react';
import { ISearchDB } from 'type/db';
import styled from 'styled-components';
import style from 'styles/styled-components/style';
import { PlatformCode, PlatformName } from 'type/common';
import { useInView } from 'react-intersection-observer';
import useCallbackRef from 'hooks/useCallbackRef';
import CoreLoading from 'shared/CoreLoading';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useCommonStore from 'store/useCommonStore';
import useListTextSelectable from 'hooks/useListTextSelectable';
import { useGetExactSearchQuery, useGetSearchQuery } from 'hooks/queries/search';
import SearchResultItem from './SearchResultItem';
import SearchResultDummyItem from './SearchResultDummyItem';

const SearchResultList = () => {
	const navigate = useNavigate();
	const [search_params] = useSearchParams();
	const keyword_query = search_params.get('keyword') ?? '';

	const { search_platform, search_filter } = useCommonStore();

	// react-query
	const { data: { results: exact_search_data = null } = {}, isLoading: exact_loading } =
		useGetExactSearchQuery(keyword_query);
	const {
		data: { pages = null } = {},
		isLoading: search_loading,
		isFetching,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useGetSearchQuery(keyword_query);

	// ref
	const [list_dom, list_ref] = useCallbackRef([pages]);

	// 무한 스크롤
	const [ref, in_view] = useInView();

	useEffect(() => {
		if (
			pages === null ||
			search_loading ||
			exact_loading ||
			isFetchingNextPage ||
			isFetching
		)
			return;

		if (in_view && hasNextPage) fetchNextPage();
	}, [
		in_view,
		pages,
		search_loading,
		exact_loading,
		isFetchingNextPage,
		isFetching,
		hasNextPage,
	]);

	/** dummy condition (영어와 숫자, 특수문자_.만 사용된 경우) */
	const dummy_regex = /[a-zA-Z\d_.]+/i;
	const dummy_test = dummy_regex.exec(keyword_query);
	const dummy_condition = dummy_test !== null && dummy_test[0] === dummy_test.input;

	/** 첫 결과 값 내에 일치하는 아이디가 없는 경우 (dummy_list 표시조건)*/
	const isSameAccount = () => {
		if (exact_search_data === null) return false;

		// return pages[0].results.find((item: ISearchDB) => item.hit_fields === '아이디 일치')
		// 	? false
		// 	: true;
		return exact_search_data?.length === 0 ? true : false;
	};

	/** 선택한 플랫폼에 따른 dummy list */
	const dummy_platform: Record<PlatformName | '전체', PlatformCode[]> = {
		전체: ['ig', 'yt', 'nb', 'tt'],
		Instagram: ['ig'],
		Youtube: ['yt'],
		'Naver Blog': ['nb'],
		TikTok: ['tt'],
	};

	const rowClick = (row_data: ISearchDB) => {
		const params = new URLSearchParams(location.search);
		params.append('rm', 'm');
		params.append('pk', row_data?.platform_pk as string);
		params.append('p', row_data?.platform_code as PlatformCode);
		navigate({ search: params.toString() });
	};

	useListTextSelectable(list_dom, [pages]);

	return (
		<List ref={list_ref}>
			{pages !== null &&
				exact_search_data !== null &&
				!search_loading &&
				!exact_loading && (
					<>
						{dummy_condition &&
							isSameAccount() &&
							dummy_platform[search_platform.name].map((item, idx) => (
								<SearchResultDummyItem
									key={idx}
									platform={item}
									keyword={keyword_query}
								/>
							))}

						{search_filter.code === 'total' &&
							exact_search_data &&
							exact_search_data.map((data_item: ISearchDB, data_idx: number) => (
								<SearchResultItem
									key={data_idx}
									data={data_item}
									_onClick={() => rowClick(data_item)}
								/>
							))}

						{pages?.map((page_item) =>
							page_item.results.map((data_item: ISearchDB, data_idx: number) => (
								<SearchResultItem
									key={data_idx}
									data={data_item}
									// _onClick={() => rowClick(data_item.platform_code, data_item.platform_pk)}
									_onClick={() => rowClick(data_item)}
								/>
							)),
						)}

						{isFetchingNextPage && (
							<style.Grid flex="center" _margin="32px">
								<CoreLoading color_type="black" />
							</style.Grid>
						)}

						<InViewItem ref={ref} />
					</>
				)}
		</List>
	);
	return null;
};

const List = styled.ul`
	width: 100%;
	height: auto;

	${({ theme }) => theme.common.flexStart};
	flex-direction: column;

	gap: 8px;
`;

const InViewItem = styled.div`
	width: 50px;
	height: 50px;
`;

export default SearchResultList;
